/**
 * `useComposeSubmit` — consumer hook for the `MessageComposePort` submit path
 * (feature 027-member-message-compose, US1).
 *
 * Resolves the `messageCompose` port from `PortsContext`, manages the submit
 * lifecycle as a small state machine (`idle` → `submitting` → `result`), and
 * returns a stable API: `{ submit, state, result }`. The component layer
 * (`WizardQuestionFlow`) owns field state and client-side required-field
 * validation; this hook owns only the async submit round-trip and its outcome.
 *
 * Missing-port contract: when no `messageCompose` slot is provided, calling
 * `submit` throws a clear missing-port error rather than failing silently —
 * the compose surface is only mounted where the port is wired (the demo
 * composition and the harness both supply it; the future Entra-only
 * composition wires the real adapter before mounting the surface).
 *
 * The hook returns the raw `ComposeSubmitResult` (carrying the server-authored
 * `memberMessage`) so the component renders the exact notification copy the
 * API authored — the success/failure copy is single-sourced server-side and
 * surfaced verbatim, not re-derived in the web layer.
 */

import { useContext, useEffect, useRef, useState } from 'react';

import { PortsContext } from '../../providers/PortsContext.js';
import type { ComposeSubmitInput, ComposeSubmitResult } from './MessageComposePort.js';

/** The submit lifecycle phase. */
export type ComposeSubmitPhase = 'idle' | 'submitting' | 'result';

/**
 * Why a THROWN submit failed (FR-016 / T044). `mapping_refused` is the api's
 * 422 UNPROCESSABLE_CONTENT (a payload the server refuses to route as-is:
 * MAPPING_REFUSED, and the US3 guidance dead-end a direct caller forces);
 * `transient` is everything else (network, server errors). RESOLVED failure
 * results (`outcome: 'failure'`, e.g. ROUTING_FAILED) do not classify here —
 * they carry the server-authored `memberMessage`.
 */
export type ComposeSubmitFailureKind = 'mapping_refused' | 'transient';

/**
 * Classify a rejection from the compose port. The check is STRUCTURAL
 * (`error.data.code`, the shape the tRPC client exposes) so the hook stays
 * vendor-free at the port boundary (P3): any adapter or test that rejects
 * with a `{ data: { code: 'UNPROCESSABLE_CONTENT' } }`-shaped error gets the
 * non-retryable notice; everything else is transient.
 */
function classifySubmitRejection(err: unknown): ComposeSubmitFailureKind {
  if (typeof err === 'object' && err !== null && 'data' in err) {
    const data = (err as { data?: unknown }).data;
    if (
      typeof data === 'object' &&
      data !== null &&
      'code' in data &&
      (data as { code?: unknown }).code === 'UNPROCESSABLE_CONTENT'
    ) {
      return 'mapping_refused';
    }
  }
  return 'transient';
}

export interface UseComposeSubmitResult {
  /**
   * Route one composed message. Resolves to the send result and stores it in
   * `result`. Throws if the `messageCompose` port is absent (a wiring bug, not
   * a user-facing failure). Re-submitting after a result is allowed (e.g. the
   * member corrects a routing failure and retries).
   *
   * In-flight guard: a call made while a previous submit is still in flight is
   * a no-op — it does NOT reach the `messageCompose` port and resolves to the
   * pending submit's promise. This prevents a duplicate legal-consent route +
   * duplicate audit event from a double activation of Lähetä (a fast double
   * click, or Enter + click). The component-layer `disabled` on the button is
   * the visible guard; this hook-level guard is the authoritative one (it holds
   * even if a caller bypasses the disabled button, e.g. a programmatic submit).
   *
   * Thrown-path contract (FR-016 / T044): a port rejection (the api's 422, a
   * network failure) is CAUGHT — the promise resolves `undefined` and the
   * classified `failureKind` is exposed in state, so the component renders a
   * failure notice instead of the rejection escaping unhandled (SC-004: no
   * silent failures). Only the missing-port wiring error still throws.
   */
  readonly submit: (input: ComposeSubmitInput) => Promise<ComposeSubmitResult | undefined>;
  /** The current lifecycle phase. */
  readonly state: ComposeSubmitPhase;
  /** The last send result, or `undefined` before the first completed submit. */
  readonly result: ComposeSubmitResult | undefined;
  /**
   * The classified reason when the last submit THREW (no `result` exists on
   * that path), or `undefined` when it resolved or none has run. Cleared when
   * a fresh submit starts, like `result`.
   */
  readonly failureKind: ComposeSubmitFailureKind | undefined;
}

export function useComposeSubmit(): UseComposeSubmitResult {
  const ports = useContext(PortsContext);
  const [state, setState] = useState<ComposeSubmitPhase>('idle');
  const [result, setResult] = useState<ComposeSubmitResult | undefined>(undefined);
  const [failureKind, setFailureKind] = useState<ComposeSubmitFailureKind | undefined>(undefined);

  // Unmount guard (R13): a submit's network window can outlive the component
  // (the member closes the wizard mid-flight, unmounting the compose subtree).
  // The post-`await` continuation must not write state on a torn-down
  // component — silent in React 19, a stale-write under StrictMode + a slow
  // adapter. Mirrors the sibling topic-load effect's `active` cancellation
  // guard in `ComposeWizard`. The returned `ComposeSubmitResult` is still handed
  // back to the (possibly already-unmounted) caller; only the STATE writes are
  // gated, so an awaiting caller keeps its result regardless.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // In-flight guard (M1): the source of truth for "a submit is already running"
  // is a ref, not the `state` value, because two rapid synchronous calls in the
  // same tick both close over the pre-`setState` `state` ('idle') — a
  // `state === 'submitting'` check inside `submit` would let the second call
  // through before React has committed the first call's render. The ref flips
  // synchronously the moment the first call enters, so the second call's
  // early-return is reliable. The pending promise is held so a guarded caller
  // still awaits the in-flight result rather than getting an undefined.
  const inFlight = useRef<Promise<ComposeSubmitResult | undefined> | null>(null);

  // No manual memoisation: the React Compiler memoises this closure, and the
  // returned `submit` is consumed only from an event handler (never an effect
  // dependency or a child prop that needs referential stability), so a
  // hand-rolled `useCallback` would be the blanket memoisation R46/R47 caution
  // against (fix dataflow first; memoise only proven hot boundaries).
  const submit = async (input: ComposeSubmitInput): Promise<ComposeSubmitResult | undefined> => {
    if (ports === null || ports.messageCompose === undefined) {
      throw new Error(
        'useComposeSubmit: the `messageCompose` port is not provided. The compose surface must be mounted inside a PortsProvider that wires it.',
      );
    }
    // Capture the narrowed port: the async IIFE below is a closure boundary, so
    // re-reading `ports.messageCompose` inside it loses the null-narrowing.
    const port = ports.messageCompose;
    // A submit is already running: do NOT route again (no duplicate consent
    // route / duplicate audit). Hand back the pending promise so an awaiting
    // caller still resolves to the in-flight result.
    if (inFlight.current !== null) {
      return inFlight.current;
    }
    setState('submitting');
    // Clear any prior result AND any prior thrown-failure kind (L6): a fresh
    // in-flight submit must not keep showing the previous send's notification.
    setResult(undefined);
    setFailureKind(undefined);
    const run = (async (): Promise<ComposeSubmitResult | undefined> => {
      try {
        const sent = await port.submit(input);
        if (mounted.current) {
          setResult(sent);
        }
        return sent;
      } catch (err: unknown) {
        // FR-016 / T044: a thrown submit (the api's 422 MAPPING_REFUSED, a
        // network failure) must still end in a member-visible failure notice,
        // never a silent unhandled rejection (SC-004). The classified kind
        // rides state; the component renders localized copy for it — the
        // server-authored `memberMessage` exists only on RESOLVED results.
        if (mounted.current) {
          setFailureKind(classifySubmitRejection(err));
        }
        return undefined;
      } finally {
        inFlight.current = null;
        if (mounted.current) {
          setState('result');
        }
      }
    })();
    inFlight.current = run;
    return run;
  };

  return { submit, state, result, failureKind };
}
